import { HttpClient } from '../shared/api/HttpClient';
import { StudioApi } from '../studio/StudioApi';
import type { LiveSessionCredential, LiveMediaState } from '../shared/media/LiveMediaAdapter';
import type { StationCommandReceipt, StationSummary, StudioCommandType, StudioSnapshot } from '../studio/types';

interface Page<T> { items: T[] }

export interface ActiveLiveSession {
  id: string;
  state: 'READY' | 'LIVE';
  leaseExpiresAt: string;
  startedAt: string | null;
  ownedByCurrentUser: boolean;
}

export interface OwnedLiveSession {
  id: string;
  state: 'READY' | 'LIVE';
  effectiveMode: 'AUTO' | 'LIVE';
  leaseToken: string;
  credential: LiveSessionCredential;
  leaseTtlSeconds: number;
}

export interface LiveGuest {
  id: string;
  displayName: string;
  state: 'CONNECTED' | 'BACKSTAGE' | 'ON_AIR' | 'MUTED' | 'DISCONNECTED';
  routingGroup: 'backstage' | 'onair';
  level: number;
  connectedAt: string | null;
  quality: 'EXCELLENT' | 'GOOD' | 'UNSTABLE' | 'POOR' | null;
  latencyMs: number | null;
  jitterMs: number | null;
  packetLossPercent: number | null;
}

export interface GuestInvitation {
  id: string;
  guestName: string;
  programName: string | null;
  invitationUrl: string;
  validFrom: string;
  validUntil: string;
}

export class MobileDjApi {
  readonly #http: HttpClient;
  readonly #studio: StudioApi;

  public constructor(http = new HttpClient({ readCsrfToken: readCsrfCookie })) {
    this.#http = http;
    this.#studio = new StudioApi(http);
  }

  public stations(): Promise<StationSummary[]> {
    return this.#http.request<Page<StationSummary>>('/stations?limit=100').then(({ items }) => items);
  }

  public login(email: string, password: string): Promise<void> {
    return this.#http.request('/auth/login', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }),
    });
  }

  public snapshot(stationId: string): Promise<StudioSnapshot> { return this.#studio.snapshot(stationId); }

  public current(stationId: string): Promise<{ session: ActiveLiveSession | null; mediaAvailable: boolean }> {
    return this.#http.request(`/stations/${stationId}/live-session`);
  }

  public start(stationId: string, expectedRuntimeRevision: number): Promise<OwnedLiveSession> {
    return this.#http.request(`/stations/${stationId}/live-sessions`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expectedRuntimeRevision }),
    });
  }

  public reconnect(stationId: string, sessionId: string, leaseToken: string): Promise<LiveSessionCredential> {
    return this.#lease(stationId, sessionId, 'credentials', { leaseToken });
  }

  public take(stationId: string, sessionId: string, leaseToken: string, expectedRuntimeRevision: number): Promise<{ state: 'LIVE'; runtimeRevision: number }> {
    return this.#lease(stationId, sessionId, 'take-control', { leaseToken, expectedRuntimeRevision });
  }

  public heartbeat(stationId: string, sessionId: string, leaseToken: string, media: LiveMediaState): Promise<{ state: 'READY' | 'LIVE'; leaseExpiresAt: string; quality: string | null }> {
    return this.#lease(stationId, sessionId, 'heartbeat', {
      leaseToken,
      quality: { latencyMs: media.latencyMs, jitterMs: media.jitterMs, packetLossPercent: media.packetLossPercent },
    });
  }

  public returnAuto(stationId: string, sessionId: string, leaseToken: string): Promise<{ state: 'ENDED'; effectiveMode: 'AUTO' }> {
    return this.#lease(stationId, sessionId, 'return-auto', { leaseToken });
  }

  public guests(stationId: string, sessionId: string, leaseToken: string): Promise<LiveGuest[]> {
    return this.#lease<{ guests: LiveGuest[] }>(stationId, sessionId, 'guests/snapshot', { leaseToken }).then(({ guests }) => guests);
  }

  public invite(stationId: string, sessionId: string, leaseToken: string, guestName: string, programName: string | null, validFrom: string, validUntil: string): Promise<GuestInvitation> {
    return this.#lease(stationId, sessionId, 'guest-invitations', { leaseToken, guestName, programName, validFrom, validUntil });
  }

  public routeGuest(stationId: string, sessionId: string, leaseToken: string, guestId: string, group: 'backstage' | 'onair'): Promise<LiveGuest> {
    return this.#lease(stationId, sessionId, `guests/${guestId}/route`, { leaseToken, group });
  }

  public muteGuest(stationId: string, sessionId: string, leaseToken: string, guestId: string, muted: boolean): Promise<LiveGuest> {
    return this.#lease(stationId, sessionId, `guests/${guestId}/mute`, { leaseToken, muted });
  }

  public levelGuest(stationId: string, sessionId: string, leaseToken: string, guestId: string, level: number): Promise<LiveGuest> {
    return this.#lease(stationId, sessionId, `guests/${guestId}/level`, { leaseToken, level });
  }

  public removeGuest(stationId: string, sessionId: string, leaseToken: string, guestId: string): Promise<void> {
    return this.#lease(stationId, sessionId, `guests/${guestId}/remove`, { leaseToken });
  }

  public command(stationId: string, sessionId: string, type: StudioCommandType, revision: number, parameters: Record<string, string | number | boolean | null> = {}): Promise<StationCommandReceipt> {
    return this.#studio.command(stationId, type, revision, parameters, crypto.randomUUID(), sessionId);
  }

  #lease<T>(stationId: string, sessionId: string, action: string, body: Record<string, unknown>): Promise<T> {
    return this.#http.request(`/stations/${stationId}/live-sessions/${sessionId}/${action}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
  }
}

function readCsrfCookie(): string | null {
  const prefix = '__Host-radio_csrf=';
  const value = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(prefix));
  return value === undefined ? null : decodeURIComponent(value.slice(prefix.length));
}
